import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExternalLink, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { SUBMISSION_STATUSES, SOURCE_LABELS } from "@shared/websiteSubmissions";
import { useSubmissionDetail, useTriage, useConvert } from "@/hooks/useWebsiteSubmissions";
import { ValuationDetail } from "./ValuationDetail";
import { BuyerDetail } from "./BuyerDetail";
import { SubscriberDetail } from "./SubscriberDetail";
import { fmtDateTime } from "./SubmissionBlocks";

/** Where a converted company's detail page lives. Mirrors CompanyDetail's map. */
function pathForCategory(cat: string | null): string {
  switch (cat) {
    case "Buyer Network":
      return "/buyers";
    case "Client":
      return "/clients";
    case "COI":
      return "/coi";
    case "Prospects":
      return "/prospects";
    case "Capital Providers":
      return "/capital-providers";
    default:
      return "/";
  }
}

const UNASSIGNED = "__unassigned__";

export function SubmissionDetail({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isLoading } = useSubmissionDetail(id);
  const triage = useTriage();
  const convert = useConvert();
  const [notes, setNotes] = useState("");

  const { data: users } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/users"],
    queryFn: async () => (await apiRequest("GET", "/api/users")).json(),
  });

  // Reset the draft whenever a different submission opens, so the previous
  // record's notes cannot be saved onto this one.
  useEffect(() => {
    setNotes(data?.internal_notes ?? "");
  }, [data?.id, data?.internal_notes]);

  const payload = (data?.payload ?? {}) as Record<string, unknown>;

  const handleConvert = async () => {
    if (!id) return;
    try {
      const r = await convert.mutateAsync(id);
      toast.success(r.action === "link" ? "Linked to the existing record." : "Created in the CRM.");
    } catch (e: any) {
      // The server's refusal messages are written to be shown as-is.
      toast.error(String(e.message ?? "").replace(/^\d+:\s*/, "") || "Conversion failed.");
    }
  };

  return (
    <Sheet open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        {isLoading || !data ? (
          <div className="space-y-3 pt-6">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <>
            <SheetHeader className="mb-4 space-y-1 border-b border-border pb-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
                {SOURCE_LABELS[data.source_type]}
              </div>
              <SheetTitle className="pr-8 text-lg leading-tight">
                {data.company_name || data.contact_email || "Submission"}
              </SheetTitle>
              <p className="text-xs text-muted-foreground">
                {fmtDateTime(data.submitted_at) ?? "—"}
              </p>
            </SheetHeader>

            {/* Triage bar */}
            <div className="mb-4 space-y-3 rounded-lg border border-border bg-muted/30 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={data.status}
                  onValueChange={(v) => triage.mutate({ id: data.id, status: v })}
                >
                  <SelectTrigger className="h-8 w-[140px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBMISSION_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s[0].toUpperCase() + s.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={data.assigned_to_id ?? UNASSIGNED}
                  onValueChange={(v) =>
                    triage.mutate({ id: data.id, assignedToId: v === UNASSIGNED ? null : v })
                  }
                >
                  <SelectTrigger className="h-8 w-[160px] text-sm">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                    {(users ?? []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {data.converted_company_id ? (
                  <Link
                    href={`${pathForCategory(data.converted_company_category)}/${data.converted_company_id}`}
                    className="ml-auto inline-flex items-center gap-1 text-sm text-primary-text hover:underline"
                  >
                    {data.converted_company_name ?? "View record"}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                ) : (
                  <Button
                    size="sm"
                    className="ml-auto h-8"
                    onClick={handleConvert}
                    disabled={convert.isPending}
                  >
                    {convert.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                    Convert
                  </Button>
                )}
              </div>

              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => {
                  if (notes !== (data.internal_notes ?? "")) {
                    triage.mutate({ id: data.id, internalNotes: notes });
                  }
                }}
                placeholder="Internal notes…"
                className="min-h-[60px] text-sm"
              />
            </div>

            {data.source_type === "valuation" && <ValuationDetail payload={payload} />}
            {data.source_type === "buyer" && <BuyerDetail payload={payload} />}
            {data.source_type === "subscriber" && <SubscriberDetail payload={payload} />}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
